import {BlockType, Filter, FilterQuery, ValidationMessage, type FilterProvider} from '@github-ui/filter'
import {escapeString} from '@github-ui/filter/utils'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'

export const singleValueFilterTypes = {
  exclusive: false,
  valueless: false,
  hasValue: false,
  multiKey: false,
  multiValue: false,
} as const

export type FilterValue = {value?: string | (() => string); displayName?: string}

export type FieldConfig<T, K extends keyof T = keyof T> = {
  filterKey: K
  queryKey: string
  parse?: (values: FilterValue[]) => T[K]
  serialize?: (value: T[K]) => string
}

const resolveValue = (val: FilterValue['value']) => (typeof val === 'function' ? val() : val)

const parseValue = (values: FilterValue[]) => resolveValue(values[0]?.value)

export const parseAsUser = (values: FilterValue[]) => {
  const login = resolveValue(values[0]?.value)
  if (!login) return undefined
  return {
    login,
    name: '',
    path: '',
    primaryAvatarUrl: '',
  }
}

const parseFilterQuery = <T extends Record<string, unknown>>(
  request: FilterQuery,
  fieldConfigs: Array<FieldConfig<T>>,
): T => {
  const filter = {} as T

  const filterBlocks = request.blocks.filter(b => b.type === BlockType.Filter)
  for (const b of filterBlocks) {
    const key = b.provider?.key
    const values = b.value?.values
    if (!key || !values?.length) continue

    const config = fieldConfigs.find(c => c.queryKey === key)
    if (!config) continue

    const parse = config.parse ?? parseValue
    const value = parse(values as FilterValue[])
    if (value !== undefined) {
      ;(filter as Record<string, unknown>)[config.filterKey as string] = value
    }
  }

  return filter
}

const serializeFilter = <T extends Record<string, unknown>>(filter: T, fieldConfigs: Array<FieldConfig<T>>): string =>
  fieldConfigs
    .map(({filterKey, queryKey, serialize = String}) => {
      const value = filter[filterKey]
      if (value == null) return null
      const stringValue = serialize(value)
      return stringValue ? `${queryKey}:${escapeString(stringValue)}` : null
    })
    .filter(Boolean)
    .join(' ')

export const getLogin = <T extends {login: string}>(v: T) => v.login
export const getName = <T extends {name: string}>(v: T) => v.name

export const timePeriodFieldConfig = {
  filterKey: 'timePeriod',
  queryKey: 'time-period',
} as const satisfies Pick<FieldConfig<{timePeriod: string}>, 'filterKey' | 'queryKey'>

export const repositoryFieldConfig = {
  filterKey: 'repository',
  queryKey: 'repo',
} as const satisfies Pick<FieldConfig<{repository: string}>, 'filterKey' | 'queryKey'>

export const organizationFieldConfig = {
  filterKey: 'organization',
  queryKey: 'org',
} as const satisfies Pick<FieldConfig<{organization: string}>, 'filterKey' | 'queryKey'>

const validateQuery = (request: FilterQuery): string[] => [
  ...request.errors,
  ...request.blocks
    .filter(b => b.type === BlockType.Text)
    .map(b => `Text will be ignored, use one of the predefined filters instead: <pre>${b.raw}</pre>`),
]

const useFocusOnChange = (ref: React.RefObject<HTMLInputElement | null>, dep: unknown) => {
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    const timeoutId = setTimeout(() => {
      const input = ref.current
      if (input) {
        input.focus()
        input.setSelectionRange(input.value.length, input.value.length)
      }
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [dep, ref])
}

const applyRequiredFields = <T extends Record<string, unknown>>(filter: T, requiredFields?: Partial<T>): T => {
  if (!requiredFields) return filter
  return {...requiredFields, ...filter} as T
}

const getOrderedConfigs = <T extends Record<string, unknown>>(
  fieldConfigs: Array<FieldConfig<T>>,
  keyOrder: string[],
): Array<FieldConfig<T>> =>
  [...fieldConfigs].sort((a, b) => {
    const aIndex = keyOrder.indexOf(a.queryKey)
    const bIndex = keyOrder.indexOf(b.queryKey)
    if (aIndex === -1 && bIndex === -1) return 0
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

type SecurityProductsFilterBarProps<T extends Record<string, unknown> & {page?: number}> = {
  id: string
  label: string
  filter: T
  fieldConfigs: Array<FieldConfig<T>>
  filterProviders: FilterProvider[]
  getIndexPath: (options: {filter: T}) => string
  requiredFields?: Partial<T>
}

export const SecurityProductsFilterBar = <T extends Record<string, unknown> & {page?: number}>({
  id,
  label,
  filter,
  fieldConfigs,
  filterProviders,
  getIndexPath,
  requiredFields,
}: SecurityProductsFilterBarProps<T>) => {
  const [keyOrder, setKeyOrder] = useState<string[]>([])
  const serializedFilter = useMemo(() => {
    const orderedConfigs = getOrderedConfigs(fieldConfigs, keyOrder)
    return serializeFilter(filter, orderedConfigs)
  }, [filter, fieldConfigs, keyOrder])

  const [inputValue, setInputValue] = useState(serializedFilter)
  useEffect(() => {
    setInputValue(serializedFilter)
  }, [serializedFilter])

  const {navigate} = useRelativeNavigation()
  const applyFilter = useCallback(
    (request: FilterQuery) => {
      const userKeyOrder = request.blocks
        .filter(b => b.type === BlockType.Filter)
        .map(b => b.provider?.key)
        .filter((k): k is string => !!k)

      const requiredKeys = requiredFields
        ? fieldConfigs.filter(c => c.filterKey in requiredFields).map(c => c.queryKey)
        : []
      const newKeyOrder = [...requiredKeys, ...userKeyOrder.filter(k => !requiredKeys.includes(k))]
      setKeyOrder(newKeyOrder)

      const parsedFilter = parseFilterQuery(request, fieldConfigs)
      const newFilter = applyRequiredFields({...parsedFilter, page: undefined}, requiredFields)
      const orderedConfigs = getOrderedConfigs(fieldConfigs, newKeyOrder)
      setInputValue(serializeFilter(newFilter, orderedConfigs))
      navigate('.', getIndexPath({filter: newFilter}), true)
    },
    [navigate, fieldConfigs, getIndexPath, requiredFields],
  )

  const [validationMessage, setValidationMessage] = useState<string[]>([])
  const handleSubmit = useCallback(
    (request: FilterQuery) => {
      const errors = validateQuery(request)
      setValidationMessage(errors)
      applyFilter(request)
    },
    [applyFilter],
  )

  const inputRef = useRef<HTMLInputElement | null>(null)
  useFocusOnChange(inputRef, filter)

  const validationMessageId = useMemo(() => `${id}-validation-message`, [id])
  const showFilterDialog = useFeatureFlag('security_products_filter_bar_afd')
  return (
    <div>
      <Filter
        id={id}
        label={label}
        onSubmit={handleSubmit}
        providers={filterProviders}
        filterValue={inputValue}
        onChange={setInputValue}
        variant={showFilterDialog ? 'full' : 'input'}
        inputRef={inputRef}
        aria-describedby={validationMessage.length > 0 ? validationMessageId : undefined}
      />
      {validationMessage.length > 0 && (
        <div className="mt-2" role="alert" aria-live="polite">
          <ValidationMessage messages={validationMessage} id={validationMessageId} />
        </div>
      )}
    </div>
  )
}
